import {useEffect} from "react";
import {
	ReadonlyURLSearchParams,
	usePathname,
	useSearchParams,
} from "next/navigation";

type UseRouterEffectParams = {
	onPathChange?: (path?: string) => void;
	onQueryParamChange?: (params?: ReadonlyURLSearchParams) => void;
	onChange?: (path?: string, params?: ReadonlyURLSearchParams) => void;
};

/**
 * Hook for subscribing to route changes.  Use this for resetting states that
 * may otherwise persist between routes (probably for page elements that persist
 * between routes but what do I know), or states that should change when parametres
 * change.
 *
 * NOTE: Be sure to only pass memoized functions to params, or the functions will
 *       be triggered on every render. :')
 * @param params takes callbacks for onPathChange (triggered when the path changes),
 *               onParamChange (triggered when parametres change), and onChange (triggered
 *               when either of the former change).
 */
const useRouterEffect: (params: UseRouterEffectParams) => void = ({
	onPathChange,
	onQueryParamChange,
	onChange,
}) => {
	const pathname = usePathname();
	const params = useSearchParams();

	useEffect(() => {
		onPathChange?.(pathname);
	}, [pathname, onPathChange]);

	useEffect(() => {
		onQueryParamChange?.(params);
	}, [params, onQueryParamChange]);

	useEffect(() => {
		onChange?.(pathname, params);
	}, [pathname, params, onChange]);
};

export default useRouterEffect;
